'use client';

import Link from '@/components/LocaleLink';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, X, User, LogOut } from 'lucide-react';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from '@/i18n/LanguageProvider';
import HeaderLangSelector from '@/components/HeaderLangSelector';
import ThemeToggle from '@/components/ThemeToggle';

/**
 * `blogVisible` llega como prop desde el layout raíz (server component), que
 * lee el flag una sola vez por petición. Es prop y no lectura propia porque
 * este componente es 'use client': leer aquí significaría una petición a
 * Firestore desde el navegador en cada carga, y el enlace parpadearía al
 * hidratar. Por defecto `true`, para que el enlace nunca desaparezca por un
 * fallo de lectura.
 */
export default function Header({ blogVisible = true }: { blogVisible?: boolean }) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const isBeta = pathname === '/beta';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userName, setUserName] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const userDoc = await getDoc(doc(db, 'users', u.uid));
          if (userDoc.exists()) {
            const d = userDoc.data();
            const name = d.guideAlias || d.displayName || d.name || u.displayName || t('nav.myAccount');
            setUserName(name);
          } else {
            setUserName(u.displayName || t('nav.myAccount'));
          }
        } catch {
          setUserName(u.displayName || t('nav.myAccount'));
        }
      } else {
        setUserName('');
      }
    });
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => { unsubscribe(); window.removeEventListener('scroll', handleScroll); };
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setIsMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 dark:bg-gray-900/95 shadow-sm' : 'bg-white/90 dark:bg-gray-900/90'} backdrop-blur-sm`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">
          <Link href="/" className="flex items-center" aria-label="IteroGO - Inicio">
            <span className="relative hidden sm:inline-block leading-none">
              <Image src="/logo_iterogo_transparent.png" alt="IteroGO - Audio Tours Autoguiados" width={280} height={71} priority />
              <sup aria-hidden="true" className="absolute top-[2px] -right-[13px] text-[13px] font-semibold leading-none text-gray-500 dark:text-gray-400">™</sup>
            </span>
            <Image src="/logo_icono.webp" alt="IteroGO" width={50} height={50} className="sm:hidden" priority />
          </Link>

          {!isBeta && (
            <nav className="hidden lg:flex items-center space-x-5 xl:space-x-8 text-sm whitespace-nowrap" aria-label={t('nav.mainNav')}>
              <Link href="/tours" className="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">{t('nav.exploreTours')}</Link>
              {blogVisible && <Link href="/blog" className="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">{t('nav.blog')}</Link>}
              <Link href="/faq" className="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">{t('nav.faq')}</Link>
              <Link href="/about" className="text-gray-700 dark:text-gray-300 hover:text-primary transition-colors">{t('nav.aboutUs')}</Link>
              <Link href="/#descargar" className="bg-accent text-white px-4 py-2 rounded-lg font-semibold hover:bg-accent/90 transition-colors">{t('home.downloadApp')}</Link>
            </nav>
          )}

          {!isBeta && (
            <div className="hidden lg:flex items-center space-x-3">
              <ThemeToggle />
              <HeaderLangSelector />
              {user ? (
                <div className="flex items-center space-x-4">
                  <Link href="/dashboard" className="flex items-center space-x-2 text-gray-700 dark:text-gray-300 hover:text-primary">
                    <User className="w-6 h-6" />
                    <span>{userName || t('nav.myAccount')}</span>
                  </Link>
                  <button onClick={handleLogout} className="text-gray-500 dark:text-gray-400 hover:text-red-500"><LogOut className="w-5 h-5" /></button>
                </div>
              ) : (
                <Link href="/login" className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.login')}</Link>
              )}
            </div>
          )}

          {!isBeta && (
            <div className="lg:hidden flex items-center gap-2">
              <ThemeToggle />
              <HeaderLangSelector />
              <button className="p-2 text-gray-600 dark:text-gray-300" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {isMenuOpen && !isBeta && (
        <div className="lg:hidden bg-white dark:bg-gray-900 border-t dark:border-gray-800">
          <nav className="flex flex-col px-4 py-4 space-y-4">
            <Link href="/tours" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.exploreTours')}</Link>
            {blogVisible && <Link href="/blog" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.blog')}</Link>}
            <Link href="/faq" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.faq')}</Link>
            <Link href="/about" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.aboutUs')}</Link>
            <Link href="/#descargar" onClick={() => setIsMenuOpen(false)} className="text-accent font-semibold hover:text-accent/80">{t('home.downloadApp')}</Link>
            <Link href="/support" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300 hover:text-primary">{t('nav.help')}</Link>
            <hr className="dark:border-gray-700" />
            {user ? (
              <>
                <Link href="/dashboard" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300">{t('nav.myPanel')}</Link>
                <button onClick={handleLogout} className="text-red-500 text-left">{t('nav.logout')}</button>
              </>
            ) : (
              <>
                <Link href="/login" onClick={() => setIsMenuOpen(false)} className="text-gray-700 dark:text-gray-300">{t('nav.login')}</Link>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
